import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 80. OBJECTIF FINAL
// Zifek Core est la FONDATION PERMANENTE de Zifek. Tous les futurs modules
// (CRM, Finance, Agenda, Commerce, Hospitality, Tourism, POS…) utilisent ses
// systèmes SANS les recréer : Users · Businesses · Customers · Contacts ·
// Employees · Products · Services · Documents · Calendar · Locations ·
// Permissions · Notifications · Events · Workflows · Files · Reviews ·
// Settings · API · Audit · Search.
//
// Le résultat visé :
//                       ZIFEK CORE
//                             │
//     ┌───────────────────────┼───────────────────────┐
//   CRM                    FINANCE                 AGENDA
//     │                       │                       │
//     ├───────────┬───────────┴───────────┬───────────┤
//  COMMERCE   HOSPITALITY              TOURISM        POS
//     └───────────┴───────────┬───────────┴───────────┘
//                          ZIFEK AI
//                             │
//                      AI TOOL ENGINE
//                             │
//                       ACTION LAYER
//                             │
//                     ALL ZIFEK MODULES
//
// Le Core doit être central, stable, sécurisé, multi-tenant, API-first et
// AI-ready. L'interface est construite AU-DESSUS de cette architecture.
// ─────────────────────────────────────────────────────────────

export interface CoreFoundation {
  id: number;
  foundation_key: string;
  label: string;
  category: string | null;
  icon: string | null;
  source_table: string | null;
  description: string | null;
  status: string | null;
  sort_order: number | null;
}

export async function listFoundations(): Promise<CoreFoundation[]> {
  const { data, error } = await supabase
    .from('core_foundation_registry')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreFoundation[];
}

export interface FoundationStatus extends CoreFoundation {
  liveCount: number | null;
  healthy: boolean;
}

/** Vérifie l'état RÉEL de chaque fondation (compte les lignes de sa table source). */
export async function fetchFoundationStatus(): Promise<FoundationStatus[]> {
  const foundations = await listFoundations();
  const out: FoundationStatus[] = [];
  for (const f of foundations) {
    let liveCount: number | null = null;
    if (f.source_table) {
      try {
        const { count, error } = await supabase
          .from(f.source_table)
          .select('*', { count: 'exact', head: true });
        if (!error) liveCount = count ?? 0;
      } catch {
        liveCount = null;
      }
    }
    out.push({ ...f, liveCount, healthy: liveCount !== null });
  }
  return out;
}

export interface FoundationStats {
  total: number;
  healthy: number;
  categories: number;
}

export async function fetchFoundationStats(): Promise<FoundationStats> {
  const status = await fetchFoundationStatus();
  return {
    total: status.length,
    healthy: status.filter((s) => s.healthy).length,
    categories: new Set(status.map((s) => s.category || 'Autre')).size,
  };
}

/** Architecture cible affichée (spec 80). */
export const FOUNDATION_LAYERS: { key: string; label: string; icon: string; desc: string; role: 'core' | 'modules' | 'ai' }[] = [
  { key: 'core', label: 'Zifek Core', icon: 'ri-stack-line', desc: 'Fondation permanente multi-tenant.', role: 'core' },
  { key: 'modules', label: 'Modules métier', icon: 'ri-apps-2-line', desc: 'CRM · Finance · Agenda · Commerce · Hospitality · Tourism · POS.', role: 'modules' },
  { key: 'ai', label: 'Zifek AI → AI Tool Engine → Action Layer', icon: 'ri-robot-2-line', desc: 'L’IA compose les modules via leurs outils sécurisés.', role: 'ai' },
];

export const CORE_READY_PRINCIPLES: { key: string; label: string; icon: string }[] = [
  { key: 'central', label: 'Plateforme centrale', icon: 'ri-focus-3-line' },
  { key: 'stable', label: 'Stable & permanente', icon: 'ri-anchor-line' },
  { key: 'secure', label: 'Sécurisée', icon: 'ri-shield-check-line' },
  { key: 'multi_tenant', label: 'Multi-tenant', icon: 'ri-building-4-line' },
  { key: 'api_first', label: 'API-first', icon: 'ri-code-s-slash-line' },
  { key: 'ai_ready', label: 'AI-ready', icon: 'ri-robot-2-line' },
];

export async function createFoundation(input: {
  foundationKey: string;
  label: string;
  category?: string | null;
  icon?: string | null;
  sourceTable?: string | null;
  description?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('core_foundation_registry').insert({
    foundation_key: input.foundationKey.trim().toLowerCase(),
    label: input.label,
    category: input.category || 'Autre',
    icon: input.icon || 'ri-stack-line',
    source_table: input.sourceTable || null,
    description: input.description || null,
    status: 'active',
    sort_order: 90,
  });
  if (error) throw error;
}

export async function deleteFoundation(id: number): Promise<void> {
  const { error } = await supabase.from('core_foundation_registry').delete().eq('id', id);
  if (error) throw error;
}